"use client";

import useSWR, { mutate as globalMutate } from "swr";
import { useAuth } from "@clerk/nextjs";
import { useCallback } from "react";
import { createFetcher } from "@/lib/api";
import type {
  Order,
  OrdersResponse,
  OrderStatus,
  Shipment,
} from "@/lib/api-types";

export function useOrders(
  page = 1,
  limit = 25,
  filters?: { status?: OrderStatus; customer_id?: string; q?: string },
) {
  const { getToken } = useAuth();
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (filters?.status) params.set("status", filters.status);
  if (filters?.customer_id) params.set("customer_id", filters.customer_id);
  if (filters?.q) params.set("q", filters.q);

  const { data, error, isLoading } = useSWR<OrdersResponse>(
    `/api/v1/orders?${params}`,
    createFetcher(getToken),
    { refreshInterval: 10_000 },
  );
  return {
    orders: data?.orders ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? page,
    isLoading,
    error,
  };
}

export function useOrder(id: string | undefined) {
  const { getToken } = useAuth();
  const { data, error, isLoading } = useSWR<Order>(
    id ? `/api/v1/orders/${id}` : null,
    createFetcher(getToken),
  );
  return { order: data, isLoading, error };
}

export function useOrderMutations() {
  const { getToken } = useAuth();

  const create = useCallback(
    async (order: Partial<Order>, shipments: Partial<Shipment>[]) => {
      const token = await getToken();
      const res = await fetch("/api/v1/orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ order, shipments }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await globalMutate((key: unknown) =>
        typeof key === "string" && key.startsWith("/api/v1/orders"),
      );
      return (await res.json()) as Order;
    },
    [getToken],
  );

  const cancel = useCallback(
    async (id: string) => {
      const token = await getToken();
      const res = await fetch(`/api/v1/orders/${id}/cancel`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error((await res.json()).error);
      await globalMutate((key: unknown) =>
        typeof key === "string" && key.startsWith("/api/v1/orders"),
      );
    },
    [getToken],
  );

  return { create, cancel };
}
